import React, { useState, useEffect } from "react";
import { Modal, Button, Table, Badge, Nav, Spinner } from "react-bootstrap";
import axios from "axios";
import config from "../../config/config";
import { getAuthToken } from "../../utils/token";
import {formatDate} from "../../utils/formatDate"
// Status mapping for API values
const STATUS_MAP = {
  not_started: {
    label: "Not Started",
    badge: <Badge bg="secondary-subtle" className="p-2 text-dark">Not Started</Badge>,
  },
  pending: {
    label: "In Progress",
    badge: (
      <Badge bg="info-subtle" className="text-dark p-2">
        In Progress
      </Badge>
    ),
  },
  delivered: {
    label: "Fulfilled",
    badge: <Badge bg="success-subtle" lassName="text-dark p-2">Fulfilled</Badge>,
  },
};

export default function POStatusModal({ show, handleClose }) {
  const [activeTab, setActiveTab] = useState("All");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    if (show) getOrders();
  }, [show]);

  useEffect(() => {
    setCurrentPage(1); 
  }, [activeTab]);

  const getOrders = async () => {
    const token = getAuthToken();
    if (!token) return;

    setLoading(true);
    try {
      const { data } = await axios.get(
        `${config.API}/purchaseorder/list`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setOrders(Array.isArray(data?.data) ? data.data : []);
    } catch (err) {
      console.error("Error fetching orders:", err.message);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter by tab
  const filteredOrders =
    activeTab === "All"
      ? orders
      : orders.filter(
          (order) =>
            STATUS_MAP[order?.plan_status]?.label === activeTab
        );
  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);
  const paginatedOrders = filteredOrders.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="xl"
      centered
      className="font_family"
    >
      <Modal.Header closeButton className="p-4">
        <strong className="fs-4">PO Status</strong>
      </Modal.Header>

      <Modal.Body className="p-4">
        {/* Tabs */}
        <Nav variant="pills" className="mb-3">
          {["All", "In Progress", "Fulfilled", "Not Started"].map(
            (tab) => (
              <Nav.Item key={tab} className="me-2">
                <Nav.Link
                  onClick={() => setActiveTab(tab)}
                  active={activeTab === tab}
                  className={`border px-3 py-2 ${
                    activeTab === tab
                      ? "bg-dark text-white border-dark"
                      : "bg-white text-dark border-secondary"
                  }`}
                  style={{ cursor: "pointer" }}
                >
                  {tab}
                </Nav.Link>
              </Nav.Item>
            )
          )}
        </Nav>

        {/* Table */}
        <div className="table-responsive">
          <Table className="align-middle table-hover mb-0">
            <thead className="table-light">
              <tr>
                <th>PO Number</th>
                <th>Product</th>
                <th>Customer</th>
                <th>Ordered</th>
                <th>Completed</th>
                <th>Due Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="text-center py-4">
                    <Spinner animation="border" size="sm" /> Loading...
                  </td>
                </tr>
              ) : paginatedOrders.length > 0 ? (
                paginatedOrders.map((order) => {
                  const statusInfo =
                    STATUS_MAP[order?.plan_status] || {};
                  return (
                    <tr key={order?.id}>
                      <td>{order?.po_number ?? "-"}</td>
                      <td>{order?.product_name ?? "-"}</td>
                      <td>{order?.consignee ?? "-"}</td>
                      <td>{order?.order_quantity ?? "-"}</td>
                      <td>{order?.produced_quantity ?? "-"}</td>
                      <td>{formatDate(order?.delivery_date ?? "-")}</td>
                      <td>{statusInfo.badge || "-"}</td>
                      <td>
                        {statusInfo.label === "Fulfilled" ? (
                          <Button
                            variant="dark"
                            size="sm"
                          >
                            Inspection Call
                          </Button>
                        ) : (
                          <Button
                            variant="dark"
                            size="sm"
                          >
                            View
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan="8"
                    className="text-center text-muted py-4"
                  >
                    No orders found.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="d-flex justify-content-end mt-3">
            <Nav>
              <Nav.Item>
                <Button
                  size="sm"
                  variant="outline-dark"
                  disabled={currentPage === 1}
                  onClick={() =>
                    setCurrentPage((prev) => prev - 1)
                  }
                >
                  Prev
                </Button>
              </Nav.Item>

              {[...Array(totalPages)].map((_, i) => (
                <Nav.Item key={i} className="mx-1">
                  <Button
                    size="sm"
                    variant={
                      currentPage === i + 1
                        ? "dark"
                        : "outline-dark"
                    }
                    onClick={() => setCurrentPage(i + 1)}
                  >
                    {i + 1}
                  </Button>
                </Nav.Item>
              ))}

              <Nav.Item>
                <Button
                  size="sm"
                  variant="outline-dark"
                  disabled={currentPage === totalPages}
                  onClick={() =>
                    setCurrentPage((prev) => prev + 1)
                  }
                >
                  Next
                </Button>
              </Nav.Item>
            </Nav>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}